import React, { useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { PropagateLoader } from "react-spinners";
import { MdDelete } from "react-icons/md";
import { toast } from "react-toastify";
import NoData from "../../atoms/NoData";
import Button from "../../atoms/Button";
import ModalConfirm from "../../molecules/ModalConfirm";
import useModal from "../../../hooks/useModal";
import { DELETE_CONTRACT } from "../../../api/apiContratos";
import { getConditionalRowStyles } from "../../../styles/expandedStyles";
import PagosExpandedTable from "./PagosExpandedTable";
import { calculateGlobalPaymentStats, sortContratosByYearDescending } from "../../../helpers/globalPaymentHelpers";
import { calculatePaymentStats } from "../../../helpers/paymentHelpers";

const ContratosTable = ({ data, refetch, loading }) => {
	const [contrato, setContrato] = useState();
	const { open: openDelete, close: closeDelete, isOpen: isOpenDelete } = useModal();
	const [expandedRow, setExpandedRow] = useState(null);

	// Ordenar contratos por año (más reciente arriba)
	const sortedData = useMemo(() => {
		return sortContratosByYearDescending(data);
	}, [data]);

	// Calcular stats globales
	const globalStats = useMemo(() => {
		return calculateGlobalPaymentStats(sortedData);
	}, [sortedData]);

	// ✅ CORREGIDO: Calcular porcentajes correctamente
	const calculatedStats = useMemo(() => {
		if (!globalStats) return null;

		// Suma total de todos los montos
		const totalMonto = 
			(globalStats.pagado?.total || 0) + 
			(globalStats.deuda?.total || 0) + 
			(globalStats.corriente?.total || 0) + 
			(globalStats.futuro?.total || 0);

		if (totalMonto === 0) {
			return {
				percentPagado: 0,
				percentAdeudado: 0,
				percentFuturo: 0,
			};
		}

		// Calcular porcentajes sobre el total
		const percentPagado = Math.round((globalStats.pagado?.total || 0) / totalMonto * 100);
		// IMPORTANTE: Adeudado = Deuda + Corriente
		const percentAdeudado = Math.round(((globalStats.deuda?.total || 0) + (globalStats.corriente?.total || 0)) / totalMonto * 100);
		const percentFuturo = Math.round((globalStats.futuro?.total || 0) / totalMonto * 100);

		return {
			percentPagado,
			percentAdeudado,
			percentFuturo,
		};
	}, [globalStats]);

	const handleDelete = async () => {
		try {
			await DELETE_CONTRACT(contrato.id);
			toast.success("Contrato eliminado correctamente");
			closeDelete();
			refetch();
		} catch (error) {
			console.error(error);
			toast.error("Error al eliminar el contrato");
		}
	};

	const handleOpenDelete = (contrato) => {
		setContrato(contrato);
		openDelete();
	};

	const formatCurrency = (value) => {
		return new Intl.NumberFormat("es-MX", {
			style: "currency",
			currency: "MXN",
		}).format(value);
	};

	const columns = [
		{
			name: "Año",
			selector: (row) => row.contractYear,
		},
		{
			name: "Monto Total Anual",
			selector: (row) => formatCurrency(row.totalAmount),
		},
		{
			name: "Status de Pagos",
			cell: (row) => {
				const stats = calculatePaymentStats(row.pagos);
				return (
					<div className="flex flex-col text-sm space-y-1">
						<div className="flex gap-2">
							<span className="text-green-600 font-semibold">✓ {stats.pagado.count}</span>
							<span className="text-red-600 font-semibold">⏰ {stats.deuda.count}</span>
							<span className="text-yellow-600 font-semibold">📅 {stats.corriente.count}</span>
							<span className="text-blue-600 font-semibold">📆 {stats.futuro.count}</span>
						</div>
					</div>
				);
			},
		},
		{
			name: "Situación Actual",
			cell: (row) => {
				const stats = calculatePaymentStats(row.pagos);
				const hayDeuda = stats.hayDeuda;
				
				if (hayDeuda) {
					return (
						<div className="flex flex-col text-xs space-y-1">
							<p className="text-red-600 font-semibold">Debe pagar: {formatCurrency(stats.pagarAhoraPara)}</p>
							<p className="text-gray-600">Futuro: {formatCurrency(stats.pendienteLuego)}</p>
						</div>
					);
				}
				
				return (
					<div className="flex flex-col text-xs">
						<p className="text-green-600">✓ Al corriente</p>
						<p className="text-gray-600">Próximo: {formatCurrency(stats.pendienteLuego)}</p>
					</div>
				);
			},
			width: "160px",
		},
		{
			name: "Acciones",
			cell: (row) => (
				<div className="flex flex-row space-x-2">
					<Button
						text={<MdDelete size={18} />}
						bgColor="red"
						width="w-10"
						onClick={() => handleOpenDelete(row)}
						title="Eliminar contrato"
					/>
				</div>
			),
			width: "100px",
		},
	];

	const columnsResponsive = [
		{
			name: "Información",
			cell: (row) => {
				const stats = calculatePaymentStats(row.pagos);
				const hayDeuda = stats.hayDeuda;

				return (
					<div className="flex flex-col space-y-3 py-2 w-full">
						<div className="flex justify-between border-b pb-2">
							<span className="font-semibold">Año:</span>
							<span>{row.contractYear}</span>
						</div>
						
						<div className="flex justify-between border-b pb-2">
							<span className="font-semibold">Total Anual:</span>
							<span>{formatCurrency(row.totalAmount * 12)}</span>
						</div>

						<div className="border-b pb-2">
							<p className="font-semibold mb-2 text-xs">Pagos:</p>
							<div className="grid grid-cols-2 gap-2 text-xs">
								<div className="flex items-center gap-1">
									<span className="text-green-600">✓</span>
									<span>{stats.pagado.count} pagados</span>
								</div>
								<div className="flex items-center gap-1">
									<span className="text-red-600">⏰</span>
									<span>{stats.deuda.count} deuda</span>
								</div>
								<div className="flex items-center gap-1">
									<span className="text-yellow-600">📅</span>
									<span>{stats.corriente.count} corriente</span>
								</div>
								<div className="flex items-center gap-1">
									<span className="text-blue-600">📆</span>
									<span>{stats.futuro.count} futuro</span>
								</div>
							</div>
						</div>

						<div className="bg-gray-50 rounded p-2">
							{hayDeuda ? (
								<div className="space-y-1">
									<p className="text-red-700 font-bold text-xs">
										⚠️ Debe: {formatCurrency(stats.pagarAhoraPara)}
									</p>
									<p className="text-gray-600 text-xs">
										Futuro: {formatCurrency(stats.pendienteLuego)}
									</p>
								</div>
							) : (
								<div className="space-y-1">
									<p className="text-green-700 font-bold text-xs">
										✓ Al corriente
									</p>
									<p className="text-gray-600 text-xs">
										Próximo: {formatCurrency(stats.pendienteLuego)}
									</p>
								</div>
							)}
						</div>
					</div>
				);
			},
		},
	];

	const ExpandedComponent = ({ data }) => (
		<PagosExpandedTable
			pagos={data.pagos || []} 
			contractId={data.id}
			refetch={refetch}
		/>
	);

	const conditionalRowStyles = getConditionalRowStyles(expandedRow);

	return (
		<div>
			{/* ESTADÍSTICAS GLOBALES - ARRIBA DE TODO */}
			{sortedData.length > 0 && globalStats && calculatedStats && (
				<div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
					<h3 className="text-lg font-bold text-gray-800 mb-4">
						📊 Estadísticas Globales - {globalStats.cantidadContratos} Contrato{globalStats.cantidadContratos !== 1 ? 's' : ''}
					</h3>

					{/* Cards de stats globales */}
					<div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
						{/* Pagados Global */}
						<div className="bg-green-50 border border-green-200 rounded p-3">
							<p className="text-xs text-gray-600 font-semibold">✓ PAGADOS</p>
							<p className="text-2xl font-bold text-green-700">{globalStats.pagado?.count || 0}</p>
							<p className="text-sm text-gray-600">{formatCurrency(globalStats.pagado?.total || 0)}</p>
							<p className="text-xs text-green-600">{calculatedStats.percentPagado}%</p>
						</div>

						{/* Deuda Global */}
						<div className={`rounded p-3 border ${globalStats.deuda?.count > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-100 border-gray-300'}`}>
							<p className="text-xs text-gray-600 font-semibold">⏰ DEUDA</p>
							<p className={`text-2xl font-bold ${globalStats.deuda?.count > 0 ? 'text-red-700' : 'text-gray-600'}`}>
								{globalStats.deuda?.count || 0}
							</p>
							<p className="text-sm text-gray-600">{formatCurrency(globalStats.deuda?.total || 0)}</p>
						</div>

						{/* Corriente Global */}
						<div className={`rounded p-3 border ${globalStats.corriente?.count > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-100 border-gray-300'}`}>
							<p className="text-xs text-gray-600 font-semibold">📅 CORRIENTE</p>
							<p className={`text-2xl font-bold ${globalStats.corriente?.count > 0 ? 'text-yellow-700' : 'text-gray-600'}`}>
								{globalStats.corriente?.count || 0}
							</p>
							<p className="text-sm text-gray-600">{formatCurrency(globalStats.corriente?.total || 0)}</p>
						</div>

						{/* Futuro Global */}
						<div className="bg-blue-50 border border-blue-200 rounded p-3">
							<p className="text-xs text-gray-600 font-semibold">📆 FUTURO</p>
							<p className="text-2xl font-bold text-blue-700">{globalStats.futuro?.count || 0}</p>
							<p className="text-sm text-gray-600">{formatCurrency(globalStats.futuro?.total || 0)}</p>
							<p className="text-xs text-blue-600">{calculatedStats.percentFuturo}%</p>
						</div>
					</div>

					{/* Información crítica global */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{/* Total a pagar */}
						<div className={`rounded p-3 border-2 ${globalStats.hayDeuda ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
							<p className={`text-sm font-semibold mb-1 ${globalStats.hayDeuda ? 'text-red-800' : 'text-green-800'}`}>
								{globalStats.hayDeuda ? '⚠️ Para Ponerse Al Corriente' : '✓ Total Pagado'}
							</p>
							<p className={`text-3xl font-bold ${globalStats.hayDeuda ? 'text-red-700' : 'text-green-700'}`}>
								{formatCurrency(globalStats.pagarAhoraPara || 0)}
							</p>
							<p className="text-xs text-gray-600 mt-1">
								{globalStats.hayDeuda 
									? `${globalStats.deuda?.count || 0} deuda + ${globalStats.corriente?.count || 0} corriente`
									: `${globalStats.pagado?.count || 0} pagos completados`
								}
							</p>
						</div>

						{/* Total pendiente (futuro) */}
						<div className="bg-blue-50 border-2 border-blue-300 rounded p-3">
							<p className="text-sm font-semibold mb-1 text-blue-800">
								📆 Pendiente Después (Futuro)
							</p>
							<p className="text-3xl font-bold text-blue-700">
								{formatCurrency(globalStats.pendienteLuego || 0)}
							</p>
							<p className="text-xs text-gray-600 mt-1">
								{globalStats.futuro?.count || 0} pagos próximos
							</p>
						</div>
					</div>

					{/* Barra de progreso - ✅ AHORA SÍ SUMA 100% */}
					<div className="mt-4">
						<p className="text-xs text-gray-600 mb-2 font-semibold">
							Progreso General ({calculatedStats.percentPagado + calculatedStats.percentAdeudado + calculatedStats.percentFuturo}%)
						</p>
						<div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden flex">
							<div 
								className="bg-green-500 h-full transition-all duration-300" 
								style={{ width: `${calculatedStats.percentPagado}%` }}
								title={`Pagados: ${calculatedStats.percentPagado}%`}
							></div>
							<div 
								className="bg-red-500 h-full transition-all duration-300" 
								style={{ width: `${calculatedStats.percentAdeudado}%` }}
								title={`Adeudado: ${calculatedStats.percentAdeudado}%`}
							></div>
							<div 
								className="bg-blue-500 h-full transition-all duration-300" 
								style={{ width: `${calculatedStats.percentFuturo}%` }}
								title={`Futuro: ${calculatedStats.percentFuturo}%`}
							></div>
						</div>
						<div className="flex gap-4 text-xs mt-2 flex-wrap">
							<span className="flex items-center gap-1">
								<span className="w-2 h-2 bg-green-500 rounded-full"></span>
								Pagados {calculatedStats.percentPagado}%
							</span>
							<span className="flex items-center gap-1">
								<span className="w-2 h-2 bg-red-500 rounded-full"></span>
								Adeudado {calculatedStats.percentAdeudado}%
							</span>
							<span className="flex items-center gap-1">
								<span className="w-2 h-2 bg-blue-500 rounded-full"></span>
								Futuro {calculatedStats.percentFuturo}%
							</span>
						</div>
					</div>
				</div>
			)}

			{/* TABLA DE CONTRATOS */}
			<div className="block md:hidden">
				<DataTable
					data={sortedData}
					progressPending={loading}
					columns={columnsResponsive}
					progressComponent={
						<div className="w-full h-20 flex justify-center items-center">
							<PropagateLoader color="red" />
						</div>
					}
					noDataComponent={<NoData />}
					pagination
					responsive
					expandableRows
					expandableRowsComponent={ExpandedComponent}
					expandableRowExpanded={(row) => row?.id === expandedRow?.id}
					onRowExpandToggled={(expanded, row) => {
						setExpandedRow(expanded ? row : null);
					}}
					conditionalRowStyles={conditionalRowStyles}
				/>
			</div>
			<div className="hidden md:block">
				<DataTable
					data={sortedData}
					progressPending={loading}
					columns={columns}
					progressComponent={
						<div className="w-full h-20 flex justify-center items-center">
							<PropagateLoader color="red" />
						</div>
					}
					noDataComponent={<NoData />}
					pagination
					expandableRows
					expandableRowsComponent={ExpandedComponent}
					expandableRowExpanded={(row) => row?.id === expandedRow?.id}
					onRowExpandToggled={(expanded, row) => {
						setExpandedRow(expanded ? row : null);
					}}
					conditionalRowStyles={conditionalRowStyles}
				/>
			</div>
			{isOpenDelete && (
				<ModalConfirm
					onClose={closeDelete}
					onConfirm={handleDelete}
					eliminacion
				/>
			)}
		</div>
	);
};

export default ContratosTable;